import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Activity, ArrowLeft, History, Inbox, LayoutDashboard, LayoutList, MessageSquare, Sparkles } from 'lucide-react';
import useMilliChat from '@/hooks/useMilliChat';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { verifyMilliOwner } from '@/lib/milliAccess.mjs';
import { fetchMilliStatus, fetchRunLedger } from '@/lib/milliData';
import { fetchApprovals, fetchDesignPreviews, fetchPostPreviews, splitApprovals } from '@/lib/approvalsData';
import { accentFor } from '@/lib/milliDepartments';
import RoomsRail from '@/components/milli/console/RoomsRail';
import ConsoleChat from '@/components/milli/console/ConsoleChat';
import ConsoleInbox from '@/components/milli/console/ConsoleInbox';
import ConsoleLedger from '@/components/milli/console/ConsoleLedger';
import ConsoleHistory from '@/components/milli/console/ConsoleHistory';
import ConsoleDashboard from '@/components/milli/console/ConsoleDashboard';

/* Milli's console — her own front door inside the app (/milli).
 *
 * Full screen, outside the dashboard chrome, same login and the same
 * milli-chat brain and approval path as everywhere else. Three columns on
 * a desk: rooms and desks on the left, the conversation in the middle, the
 * approval inbox and run ledger on the right, with History (everything she
 * ever did, oldest first) a toggle away in that same column. On a phone the
 * same five live behind a tab bar. The whole room takes the color of the desk that
 * answered last (--milli-accent), Milli's copper until one has.
 *
 * Palette, as before: charcoal steel ground (#17181a / #1e1f21),
 * dark-bronze hairlines (#3a332a), weathered copper (#c9873e). */

const REFRESH_MS = 90 * 1000;

/* Keep Milli unmounted until Supabase verifies this session's owner access.
 * Bind results to both user and token so account/session changes close the
 * console immediately, before the next effect runs. Errors fail closed. */
export default function MilliConsolePage() {
  const { t } = useTranslation();
  const { user, session, profile, signOut } = useAuth();
  const [access, setAccess] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const userId = user?.id;
  const token = session?.access_token;
  const current = !!access && access.userId === userId && access.token === token;
  const owner = current ? access.owner : null;
  const checkFailed = current && access.failed;

  useEffect(() => {
    let cancelled = false;
    setAccess(null);
    const finish = (owner, failed = false) => {
      if (!cancelled) setAccess({ userId, token, owner, failed });
    };
    // A stalled network request should offer retry, never mount the console.
    const timer = setTimeout(() => {
      finish(false, true);
      cancelled = true;
    }, 15000);
    (async () => {
      try {
        const allowed = userId && token
          ? await verifyMilliOwner(supabase, userId)
          : false;
        finish(allowed);
      } catch {
        finish(false, true);
      } finally {
        clearTimeout(timer);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userId, token, attempt]);

  if (owner === null) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-[#131416] text-stone-200"
        role="status" data-testid="milli-console-verifying">
        {t('milli.console.verifyingAccess', { defaultValue: 'Verifying Milli access…' })}
      </div>
    );
  }

  if (checkFailed) {
    return (
      <div className="flex h-[100dvh] flex-col items-center justify-center gap-4 bg-[#131416] px-6 text-stone-200"
        role="alert" data-testid="milli-console-access-error">
        <p>{t('milli.console.accessError', { defaultValue: 'Could not verify Milli access. Try again.' })}</p>
        <button type="button" onClick={() => { setAccess(null); setAttempt((n) => n + 1); }}
          className="rounded-[10px] border border-[#6a6a6a] px-4 py-2"
          data-testid="milli-console-access-retry">
          {t('milli.console.retryAccess', { defaultValue: 'Retry' })}
        </button>
        <Link to="/dashboard">{t('milli.console.notOwner.back')}</Link>
      </div>
    );
  }

  if (owner === false) {
    const name = profile?.full_name || user?.email || '';
    return (
      <div
        className="flex h-[100dvh] flex-col items-center justify-center bg-[#131416] px-6 text-stone-200"
        data-testid="milli-console-not-owner"
      >
        <div className="w-full max-w-[520px] rounded-[14px] border border-[#3a332a] bg-[#1b1c1e] p-6 shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
          <div className="flex items-center gap-2 font-mono text-sm tracking-[0.28em] text-[#c9873e]">
            <Sparkles size={16} /> MILLI
          </div>
          <h1 className="mt-4 font-display text-[22px] leading-snug text-[#EDEBE6]">{t('milli.console.notOwner.title')}</h1>
          <p className="mt-3 text-sm text-stone-400" data-testid="milli-console-signed-in-as">
            {t('milli.console.notOwner.signedInAs', { name })}
          </p>
          <p className="mt-1 text-sm text-stone-400">{t('milli.console.notOwner.hint')}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/dashboard"
              className="rounded-[10px] bg-[#C9A24B] px-4 py-2 text-[12px] font-bold uppercase tracking-[0.12em] text-[#141210]"
              data-testid="milli-console-not-owner-back"
            >
              {t('milli.console.notOwner.back')}
            </Link>
            <button
              type="button"
              onClick={() => signOut?.()}
              className="rounded-[10px] border border-[#6a6a6a] px-4 py-2 text-[12px] font-bold uppercase tracking-[0.12em] text-[#EDEBE6]"
              data-testid="milli-console-not-owner-signout"
            >
              {t('milli.console.notOwner.signOut')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <MilliConsoleShell t={t} />;
}

function MilliConsoleShell({ t }) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [tab, setTab] = useState('home'); // phone: home | chat | rooms | inbox | ledger | history
  const [desk, setDesk] = useState('inbox'); // desk, right column: inbox | history
  const [view, setView] = useState('dashboard'); // desk: dashboard (the front door, 084) | console (rooms, chat, inbox)
  const [refreshTick, setRefreshTick] = useState(0); // bumps on every panel refresh so History refetches too

  const [panels, setPanels] = useState({ runs: [], tasks: [], loading: true });
  const [approvals, setApprovals] = useState({
    pending: [], decidedCount: 0, previews: {}, postPreviews: {}, loading: true, error: null,
  });

  // Each panel degrades on its own; a failed read never blanks the chat.
  const refreshPanels = useCallback(async () => {
    const [status, ledger, inbox] = await Promise.allSettled([
      fetchMilliStatus(),
      fetchRunLedger(30),
      (async () => {
        const rows = await fetchApprovals();
        const { pending, decided } = splitApprovals(rows);
        const ids = pending.map((r) => r.id);
        const [previews, postPreviews] = await Promise.all([fetchDesignPreviews(ids), fetchPostPreviews(ids)]);
        return { pending, decidedCount: decided.length, previews, postPreviews };
      })(),
    ]);
    setPanels({
      runs: ledger.status === 'fulfilled' ? ledger.value : [],
      tasks: status.status === 'fulfilled' ? status.value.tasks : [],
      loading: false,
    });
    setApprovals((cur) =>
      inbox.status === 'fulfilled'
        ? { ...inbox.value, loading: false, error: null }
        : { ...cur, loading: false, error: inbox.reason?.message || t('approvals.loadError') }
    );
    setRefreshTick((n) => n + 1);
  }, [t]);

  const chat = useMilliChat({ onTurn: refreshPanels, sendErrorText: t('milli.sendError') });
  const accent = accentFor(chat.department);

  useEffect(() => {
    refreshPanels();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refreshPanels();
    }, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshPanels();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshPanels]);

  useEffect(() => {
    // While the console is open, the document wears Milli's app identity:
    // her manifest (name "Milli", start_url /milli, shield icon) and her
    // apple-touch-icon. So "Add to Home Screen" from here installs a Milli
    // app that opens straight to her console, full screen, while the rest
    // of the dashboard keeps the K.O. CO+RE identity. Restored on unmount.
    const manifest = document.querySelector('link[rel="manifest"]');
    const apples = Array.from(document.querySelectorAll('link[rel="apple-touch-icon"]'));
    const prevManifest = manifest?.getAttribute('href');
    const prevApples = apples.map((l) => l.getAttribute('href'));
    if (manifest) manifest.setAttribute('href', '/milli/manifest.json');
    apples.forEach((l) => l.setAttribute('href', '/milli/apple-touch-icon.png'));
    const prevTitle = document.title;
    document.title = 'Milli';
    return () => {
      if (manifest && prevManifest) manifest.setAttribute('href', prevManifest);
      apples.forEach((l, i) => {
        if (prevApples[i]) l.setAttribute('href', prevApples[i]);
      });
      document.title = prevTitle;
    };
  }, []);

  const department = chat.department;
  const pendingCount = approvals.pending.length;

  const tabs = [
    { id: 'home', icon: LayoutDashboard, label: t('milli.console.tabs.home') },
    { id: 'chat', icon: MessageSquare, label: t('milli.console.tabs.chat') },
    { id: 'rooms', icon: LayoutList, label: t('milli.console.tabs.rooms') },
    { id: 'inbox', icon: Inbox, label: t('milli.console.tabs.inbox'), badge: pendingCount },
    { id: 'ledger', icon: Activity, label: t('milli.console.tabs.ledger') },
    { id: 'history', icon: History, label: t('milli.console.tabs.history') },
  ];
  const showHistory = isDesktop ? desk === 'history' : tab === 'history';
  const openInbox = () => {
    setTab('inbox');
    setDesk('inbox');
    setView('console');
  };
  const openChat = () => {
    setTab('chat');
    setView('console');
  };
  const showDashboard = isDesktop ? view === 'dashboard' : tab === 'home';

  return (
    <div
      className="flex h-[100dvh] flex-col bg-[#131416] text-stone-200"
      style={{ '--milli-accent': accent }}
      data-testid="milli-console"
      data-department={department?.id ?? ''}
    >
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-[#3a332a] bg-[#151618] px-4 py-2.5">
        <Link
          to="/dashboard"
          className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-stone-500 hover:text-[color:var(--milli-accent)] transition-colors"
          data-testid="milli-console-back"
        >
          <ArrowLeft size={12} />
          <span className="hidden sm:inline">{t('milli.console.back')}</span>
        </Link>
        <span className="h-4 w-px bg-[#3a332a]" />
        <Sparkles size={18} className="text-[color:var(--milli-accent)] transition-colors duration-500" />
        <div className="leading-tight">
          <div className="font-mono text-sm tracking-[0.28em] text-[color:var(--milli-accent)] transition-colors duration-500">
            MILLI
          </div>
          <div className="hidden sm:block font-mono text-[10px] tracking-wider text-stone-500">
            {t('milli.console.subtitle')}
          </div>
        </div>
        {department ? (
          <span
            className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ color: department.hex, borderColor: `${department.hex}66` }}
            data-testid="milli-department"
            title={department.pillar}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: department.hex }} />
            {department.name}
            {department.secondary ? (
              <span className="hidden sm:inline text-stone-500 normal-case tracking-normal">+ {department.secondary.name}</span>
            ) : null}
          </span>
        ) : null}
        {/* Desk: the front door is the dashboard; the console is one click away. */}
        <div className="hidden lg:flex items-center gap-1 rounded-full border border-[#3a332a] p-0.5" data-testid="milli-view-toggle">
          {[
            { id: 'dashboard', icon: LayoutDashboard, label: t('milli.console.tabs.home') },
            { id: 'console', icon: MessageSquare, label: t('milli.console.tabs.chat') },
          ].map(({ id, icon: Icon, label }) => {
            const on = view === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setView(id)}
                aria-pressed={on}
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                  on ? 'bg-[#2c2e33] text-[color:var(--milli-accent)]' : 'text-stone-500 hover:text-stone-300'
                }`}
                data-testid={`milli-view-${id}`}
              >
                <Icon size={12} />
                {label}
              </button>
            );
          })}
        </div>
        {pendingCount > 0 ? (
          <button
            type="button"
            onClick={openInbox}
            className="rounded-full bg-amber-400/15 border border-amber-400/40 px-2 py-0.5 font-mono text-[10px] text-amber-300 hover:bg-amber-400/25 transition-colors"
            data-testid="milli-console-pending"
          >
            {pendingCount} {t('milli.dock.pending')}
          </button>
        ) : null}
        <span className="ml-auto flex items-center gap-2 font-mono text-[11px] text-emerald-400">
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="hidden sm:inline">{t('milli.online')}</span>
        </span>
      </header>

      {/* Body */}
      <div className="flex flex-1 min-h-0">
        {showDashboard ? (
          <main className="flex-1 min-w-0 min-h-0 overflow-y-auto" data-testid="milli-dashboard-main">
            <ConsoleDashboard
              t={t}
              onOpenChat={openChat}
              onOpenInbox={openInbox}
              pendingCount={pendingCount}
              refreshTick={refreshTick}
              isDesktop={isDesktop}
            />
          </main>
        ) : null}
        {!showDashboard && (isDesktop || tab === 'rooms') ? (
          <aside className="w-full min-h-0 overflow-hidden lg:w-[260px] lg:shrink-0 lg:border-r lg:border-[#3a332a]">
            <RoomsRail chat={chat} t={t} onPicked={() => setTab('chat')} />
          </aside>
        ) : null}
        {!showDashboard && (isDesktop || tab === 'chat') ? (
          <main className="flex flex-1 min-w-0 min-h-0 flex-col bg-gradient-to-b from-[#1b1c1e] to-[#131416]">
            <ConsoleChat chat={chat} t={t} />
          </main>
        ) : null}
        {!showDashboard && (isDesktop || tab === 'inbox' || tab === 'ledger' || tab === 'history') ? (
          <aside className="w-full min-h-0 overflow-y-auto bg-[#141517] lg:w-[400px] xl:w-[440px] lg:shrink-0 lg:border-l lg:border-[#3a332a]">
            {/* Desk only: the right column is the inbox and ledger, or the history. */}
            <div className="hidden lg:flex items-center gap-1 border-b border-[#3a332a] px-4 py-2" data-testid="milli-desk-toggle">
              {[
                { id: 'inbox', icon: Inbox, label: t('milli.console.inbox'), badge: pendingCount },
                { id: 'history', icon: History, label: t('milli.console.history.title') },
              ].map(({ id, icon: Icon, label, badge }) => {
                const on = desk === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setDesk(id)}
                    aria-pressed={on}
                    className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                      on
                        ? 'border-[color:var(--milli-accent)] text-[color:var(--milli-accent)]'
                        : 'border-transparent text-stone-500 hover:text-stone-300'
                    }`}
                    data-testid={`milli-desk-${id}`}
                  >
                    <Icon size={12} />
                    {label}
                    {badge ? <span className="rounded-full bg-amber-400/15 border border-amber-400/40 px-1.5 text-[9px] text-amber-300">{badge}</span> : null}
                  </button>
                );
              })}
            </div>
            {showHistory ? (
              <ConsoleHistory chat={chat} onOpenChat={() => setTab('chat')} onOpenInbox={openInbox} refreshTick={refreshTick} t={t} />
            ) : (
              <>
                {isDesktop || tab === 'inbox' ? (
                  <ConsoleInbox approvals={approvals} onChanged={refreshPanels} t={t} />
                ) : null}
                {isDesktop || tab === 'ledger' ? (
                  <ConsoleLedger runs={panels.runs} tasks={panels.tasks} loading={panels.loading} t={t} />
                ) : null}
              </>
            )}
          </aside>
        ) : null}
      </div>

      {/* Phone tab bar */}
      {!isDesktop ? (
        <nav className="grid grid-cols-6 border-t border-[#3a332a] bg-[#151618]" data-testid="milli-console-tabs">
          {tabs.map(({ id, icon: Icon, label, badge }) => {
            const on = tab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`relative flex flex-col items-center gap-0.5 py-2 font-mono text-[9px] uppercase tracking-wider transition-colors ${
                  on ? 'text-[color:var(--milli-accent)]' : 'text-stone-500'
                }`}
                data-testid={`milli-console-tab-${id}`}
                aria-current={on ? 'page' : undefined}
              >
                <Icon size={16} />
                {label}
                {badge ? (
                  <span className="absolute right-[22%] top-1 min-w-[16px] rounded-full bg-amber-400 px-1 text-[9px] font-bold leading-4 text-[#17181a]">
                    {badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
      ) : null}
    </div>
  );
}
